// Session 74z8 — the rebrand and the getstede.com migration.
//
// Stede is the product. The company using it is the tenant. Before this file
// the tenant's name was a string literal in 17 files, which is exactly what
// the brand identity gate calls the multi-tenant seam: hardcoding "D&R Signs"
// into the chrome is the thing that stops a second customer signing up
// without a rebuild.
//
// Why a module-level cache and not a React context. `printLabel.js` and
// `canonM5Export.js` are plain utility modules, not components, and they are
// the call sites that matter most — a printed label carries whatever name it
// was given for as long as that label is stuck to a shelf. A context cannot
// reach them without threading a parameter through every print call site and
// every component that calls one. AuthContext fills this cache once, after
// the profile check passes; everything else reads it synchronously.
//
// The fallback is load-bearing in one place that is easy to miss: the sign-in
// screen renders before anyone is authenticated, and `company_settings`'
// SELECT policy is roles={authenticated}, so anon genuinely cannot read the
// tenant name. Sign-in shows the fallback. For a single-tenant deployment
// that is correct; a second customer needs either a per-tenant domain or an
// anon-readable public settings view, and that is a schema decision, not this
// file's to make.

// The product. Never tenant-configurable — this one is ours.
export const PRODUCT_NAME = 'Stede';

// Gate-approved copy. Kept here rather than inline so the strapline and the
// descriptor cannot drift apart across the three screens that use them.
export const PRODUCT_UNIT = 'Field Operations';   // topbar sub-line
export const PRODUCT_DESCRIPTOR = 'Every part in place.';  // sign-in, under the wordmark
export const PRODUCT_STRAPLINE = 'Get steady';    // sign-in button, the action position

export const TENANT_FALLBACK = 'D&R Signs';

let cachedCompanyName = TENANT_FALLBACK;

// Called by AuthContext once company_settings loads. An empty or whitespace
// name falls back rather than rendering a blank topbar — company_name is NOT
// NULL in the schema, but a future tenant provisioning path could still write
// an empty string, and a blank brand block reads as a broken page.
export function setCompanyName(name) {
  cachedCompanyName = (name && String(name).trim()) || TENANT_FALLBACK;
}

// Synchronous by design — see the note above about print utilities.
export function getCompanyName() {
  return cachedCompanyName;
}

// The tenant name is one row that does not change during a session, but the
// thing that triggers the read — fetchProfile — runs from both initAuth and
// onAuthStateChange, and React StrictMode double-invokes both in development.
// Measured live at 74z8 before this guard: six identical
// `company_settings?select=company_name` round trips on a single page load, at
// ~100ms each, on the sign-in path. Mobile is the priority surface, so that is
// not a rounding error.
//
// The guard is module-level rather than a ref in AuthContext on purpose: a ref
// is recreated when StrictMode unmounts and remounts the provider, which is
// exactly the case that produced the duplicates. `inFlight` collapses callers
// that arrive while the first request is still open; `loaded` collapses every
// caller after it.
let loaded = false;
let inFlight = null;

export async function loadCompanyNameOnce(loader) {
  if (loaded) return cachedCompanyName;
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(loader)
      .then(name => {
        // Only a real value latches. A failed read leaves `loaded` false so a
        // later sign-in can retry, rather than pinning the fallback for the
        // life of the tab.
        if (name) {
          setCompanyName(name);
          loaded = true;
        }
        return cachedCompanyName;
      })
      .catch(() => cachedCompanyName)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

export function resetCompanyName() {
  cachedCompanyName = TENANT_FALLBACK;
  loaded = false;
  inFlight = null;
  cachedPrintOnReceive = null;
}

// #342, Session 78c. `company_settings.print_on_receive_default` is `bool NOT
// NULL DEFAULT true` and has had an admin toggle since #21 at Session 72. It was
// wired to nothing: no receive sheet's caller passed `printOnReceiveDefault`, so
// an admin turning it off changed no behaviour anywhere — the shape of defect
// that gets reported as "the app ignored my setting".
//
// It lives here rather than in a new context or a fetch of its own because
// `company_settings` is ONE ROW read ONCE PER SESSION, by the query AuthContext
// already runs for the tenant name. A second column on that select costs nothing;
// a second query would cost a round trip on the sign-in path, which is the
// measurement that produced this file's caching guard in the first place.
//
// null means "not loaded yet", which is deliberately distinct from false. The
// receive sheets treat null as the schema default (true) rather than as off, so a
// slow or failed settings read cannot silently disable printing for a branch.
let cachedPrintOnReceive = null;

export function setPrintOnReceiveDefault(value) {
  cachedPrintOnReceive = typeof value === 'boolean' ? value : null;
}

// Returns true when the setting has not loaded, matching the column's own
// NOT NULL DEFAULT true. Never returns null — callers should not have to
// distinguish "off" from "unknown" on a print toggle.
export function getPrintOnReceiveDefault() {
  return cachedPrintOnReceive == null ? true : cachedPrintOnReceive;
}
